// ================================================================= //
//          Room Checklists — Client-side Static Data                 //
// ================================================================= //
// This file mirrors the backend room_checklists.py for instant       //
// client-side checklist rendering without a server round-trip.       //
// Room types align with app/analysis/extractors/room_patterns.py.   //
// ================================================================= //

import type { ChecklistElement, RoomChecklist } from '../types';

// --- Room Type Options for Selectors ---

export const ROOM_TYPES: { value: string; label: string }[] = [
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'master_bedroom', label: 'Master Bedroom' },
  { value: 'bedroom', label: 'Bedroom' },
  { value: 'master_bathroom', label: 'Master Bathroom' },
  { value: 'bathroom', label: 'Bathroom' },
  { value: 'living_room', label: 'Living Room' },
  { value: 'dining_room', label: 'Dining Room' },
  { value: 'hallway', label: 'Hallway' },
  { value: 'laundry_room', label: 'Laundry Room' },
  { value: 'closet', label: 'Closet' },
  { value: 'office', label: 'Office / Den' },
  { value: 'garage', label: 'Garage' },
  { value: 'roof', label: 'Roof' },
  { value: 'exterior', label: 'Exterior' },
  { value: 'general_conditions', label: 'General Conditions' },
  { value: 'hvac', label: 'HVAC / Mechanical' },
  { value: 'stairs', label: 'Stairs' },
  { value: 'porch', label: 'Porch / Patio' },
  { value: 'sitting_room', label: 'Sitting Room / Den' },
  { value: 'sunroom', label: 'Sunroom' },
  { value: 'storage', label: 'Storage / Shed' },
  { value: 'contents', label: 'Contents' },
  { value: 'other', label: 'Other' },
];

// --- Damage Type Options ---

export const DAMAGE_TYPES: { value: string; label: string }[] = [
  { value: 'water', label: 'Water Damage' },
  { value: 'fire', label: 'Fire Damage' },
  { value: 'smoke', label: 'Smoke Damage' },
  { value: 'wind', label: 'Wind Damage' },
  { value: 'hurricane', label: 'Hurricane' },
  { value: 'hail', label: 'Hail Damage' },
  { value: 'flood', label: 'Flood / NFIP' },
  { value: 'mold', label: 'Mold Damage' },
  { value: 'storm', label: 'Storm Damage' },
  { value: 'vandalism', label: 'Vandalism' },
  { value: 'other', label: 'Other' },
];

// --- Claim Type Options ---

export const CLAIM_TYPES: { value: string; label: string }[] = [
  { value: 'homeowner', label: 'Homeowner' },
  { value: 'flood', label: 'Flood / NFIP' },
  { value: 'commercial', label: 'Commercial' },
];

// --- Room Checklists ---
// Each room type has a list of elements that a complete scope should address.
// Elements have keyword arrays for detecting mentions in free-text descriptions,
// and sub_prompts that guide users on what details matter for that element.

const el = (
  id: string,
  label: string,
  keywords: string[],
  sub_prompts: string[]
): ChecklistElement => ({ id, label, keywords, sub_prompts });

export const ROOM_CHECKLISTS: Record<string, RoomChecklist> = {
  kitchen: {
    elements: [
      el('k_ceiling', 'Ceiling', ['ceiling', 'overhead', 'popcorn', 'texture'], [
        'Type (drywall, plaster, popcorn)?',
        'Height?',
        'Any staining or sagging?',
      ]),
      el('k_walls', 'Walls', ['wall', 'walls', 'drywall', 'plaster'], [
        'How many walls affected?',
        'Damage height from floor?',
        'Type (drywall, plaster, tile)?',
      ]),
      el('k_flooring', 'Flooring', ['floor', 'flooring', 'tile', 'vinyl', 'hardwood', 'laminate', 'lvp'], [
        'Material type (tile, vinyl, hardwood, LVP)?',
        'Square footage affected?',
        'Need underlayment replacement?',
      ]),
      el('k_cabinets', 'Cabinets', ['cabinet', 'cabinets', 'upper', 'lower', 'base'], [
        'Upper or lower cabinets?',
        'Linear feet?',
        'Material (wood, laminate, custom)?',
      ]),
      el('k_countertops', 'Countertops', ['counter', 'countertop', 'granite', 'quartz', 'laminate'], [
        'Material type?',
        'Linear feet?',
        'Include backsplash?',
      ]),
      el('k_appliances', 'Appliances', ['appliance', 'stove', 'oven', 'fridge', 'refrigerator', 'dishwasher', 'microwave'], [
        'Which appliances affected?',
        'Need disconnect/reconnect?',
        'Replacement or cleaning?',
      ]),
      el('k_plumbing', 'Plumbing', ['plumbing', 'pipe', 'faucet', 'sink', 'disposal', 'drain'], [
        'Sink type?',
        'Garbage disposal?',
        'Any pipe repairs needed?',
      ]),
      el('k_electrical', 'Electrical', ['electrical', 'outlet', 'switch', 'light', 'lighting', 'gfci', 'fixture'], [
        'Number of outlets/switches?',
        'Light fixtures affected?',
        'GFCI outlets needed?',
      ]),
      el('k_paint', 'Paint / Finish', ['paint', 'prime', 'primer', 'finish', 'stain', 'seal'], [
        'Walls, ceiling, or both?',
        'Texture match needed?',
        'Number of coats?',
      ]),
      el('k_baseboard', 'Baseboard / Trim', ['baseboard', 'trim', 'molding', 'casing', 'crown'], [
        'Linear feet?',
        'Material and profile?',
        'Crown molding included?',
      ]),
    ],
  },

  master_bedroom: {
    elements: [
      el('mb_ceiling', 'Ceiling', ['ceiling', 'overhead', 'texture', 'fan'], [
        'Type and height?',
        'Ceiling fan present?',
        'Any water stains?',
      ]),
      el('mb_walls', 'Walls', ['wall', 'walls', 'drywall'], [
        'Number of walls affected?',
        'Damage height?',
        'Any wallpaper?',
      ]),
      el('mb_flooring', 'Flooring', ['floor', 'flooring', 'carpet', 'hardwood', 'laminate', 'pad'], [
        'Material type?',
        'Square footage?',
        'Pad replacement needed?',
      ]),
      el('mb_closet', 'Closet', ['closet', 'walk-in', 'wardrobe', 'shelf', 'shelving'], [
        'Walk-in or reach-in?',
        'Shelving/rod system?',
        'Square footage?',
      ]),
      el('mb_paint', 'Paint / Finish', ['paint', 'prime', 'finish', 'texture'], [
        'Walls, ceiling, or both?',
        'Texture match?',
        'Accent walls?',
      ]),
      el('mb_baseboard', 'Baseboard / Trim', ['baseboard', 'trim', 'molding', 'casing'], [
        'Linear feet?',
        'Material and profile?',
      ]),
      el('mb_electrical', 'Electrical', ['electrical', 'outlet', 'switch', 'light', 'fixture'], [
        'Outlets/switches affected?',
        'Light fixtures?',
      ]),
      el('mb_door', 'Door(s)', ['door', 'entry', 'interior'], [
        'Interior or exterior door?',
        'Size and type?',
        'Hardware replacement?',
      ]),
      el('mb_window', 'Windows', ['window', 'glass', 'blinds', 'curtain'], [
        'Number of windows?',
        'Window coverings affected?',
      ]),
    ],
  },

  bedroom: {
    elements: [
      el('br_ceiling', 'Ceiling', ['ceiling', 'overhead', 'texture'], [
        'Type and height?',
        'Any staining or damage?',
      ]),
      el('br_walls', 'Walls', ['wall', 'walls', 'drywall'], [
        'Number of walls affected?',
        'Damage height?',
      ]),
      el('br_flooring', 'Flooring', ['floor', 'flooring', 'carpet', 'hardwood', 'pad'], [
        'Material type?',
        'Square footage?',
        'Pad replacement?',
      ]),
      el('br_closet', 'Closet', ['closet', 'shelf', 'shelving', 'rod'], [
        'Type (reach-in, walk-in)?',
        'Shelving system?',
      ]),
      el('br_paint', 'Paint / Finish', ['paint', 'prime', 'finish'], [
        'Walls, ceiling, or both?',
        'Texture match?',
      ]),
      el('br_baseboard', 'Baseboard / Trim', ['baseboard', 'trim', 'molding'], [
        'Linear feet?',
        'Material?',
      ]),
      el('br_door', 'Door', ['door', 'entry'], [
        'Type and size?',
        'Hardware?',
      ]),
      el('br_window', 'Windows', ['window', 'glass', 'blinds'], [
        'Number of windows?',
        'Coverings affected?',
      ]),
    ],
  },

  master_bathroom: {
    elements: [
      el('mbr_ceiling', 'Ceiling', ['ceiling', 'overhead', 'exhaust', 'vent'], [
        'Type and height?',
        'Exhaust fan present?',
        'Moisture damage?',
      ]),
      el('mbr_walls', 'Walls', ['wall', 'walls', 'drywall', 'tile', 'backer'], [
        'Walls affected?',
        'Tile or drywall?',
        'Backer board needed?',
      ]),
      el('mbr_flooring', 'Flooring', ['floor', 'flooring', 'tile', 'vinyl', 'marble'], [
        'Material type?',
        'Square footage?',
        'Subfloor damage?',
      ]),
      el('mbr_shower', 'Shower / Tub', ['shower', 'tub', 'bathtub', 'surround', 'enclosure', 'glass door'], [
        'Shower, tub, or combo?',
        'Surround material?',
        'Glass enclosure?',
      ]),
      el('mbr_vanity', 'Vanity / Sink', ['vanity', 'sink', 'cabinet', 'countertop', 'mirror'], [
        'Single or double vanity?',
        'Countertop material?',
        'Mirror replacement?',
      ]),
      el('mbr_toilet', 'Toilet', ['toilet', 'commode', 'wax ring', 'flange'], [
        'Replacement or reset?',
        'Wax ring/flange?',
      ]),
      el('mbr_plumbing', 'Plumbing', ['plumbing', 'pipe', 'faucet', 'valve', 'drain'], [
        'Fixture replacement?',
        'Supply line repairs?',
        'Drain issues?',
      ]),
      el('mbr_electrical', 'Electrical', ['electrical', 'outlet', 'gfci', 'light', 'fixture', 'exhaust fan'], [
        'GFCI outlets?',
        'Exhaust fan?',
        'Light fixtures?',
      ]),
      el('mbr_paint', 'Paint / Finish', ['paint', 'prime', 'finish', 'moisture'], [
        'Moisture-resistant paint?',
        'Walls and ceiling?',
      ]),
      el('mbr_accessories', 'Accessories', ['towel bar', 'toilet paper', 'accessory', 'hook', 'grab bar'], [
        'Towel bars, hooks?',
        'Toilet paper holder?',
        'Grab bars?',
      ]),
    ],
  },

  bathroom: {
    elements: [
      el('ba_ceiling', 'Ceiling', ['ceiling', 'overhead', 'exhaust'], [
        'Type?',
        'Exhaust fan?',
      ]),
      el('ba_walls', 'Walls', ['wall', 'walls', 'drywall', 'tile'], [
        'Tile or drywall?',
        'Walls affected?',
      ]),
      el('ba_flooring', 'Flooring', ['floor', 'flooring', 'tile', 'vinyl'], [
        'Material type?',
        'Square footage?',
      ]),
      el('ba_shower', 'Shower / Tub', ['shower', 'tub', 'bathtub', 'surround'], [
        'Type (shower, tub, combo)?',
        'Surround material?',
      ]),
      el('ba_vanity', 'Vanity / Sink', ['vanity', 'sink', 'cabinet', 'mirror'], [
        'Type and size?',
        'Mirror?',
      ]),
      el('ba_toilet', 'Toilet', ['toilet', 'commode'], [
        'Replacement or reset?',
      ]),
      el('ba_plumbing', 'Plumbing', ['plumbing', 'pipe', 'faucet', 'drain'], [
        'Fixtures affected?',
        'Pipe repairs?',
      ]),
      el('ba_electrical', 'Electrical', ['electrical', 'outlet', 'gfci', 'light'], [
        'GFCI outlets?',
        'Light fixtures?',
      ]),
      el('ba_paint', 'Paint / Finish', ['paint', 'prime', 'finish'], [
        'Moisture-resistant?',
      ]),
    ],
  },

  living_room: {
    elements: [
      el('lr_ceiling', 'Ceiling', ['ceiling', 'overhead', 'texture', 'fan', 'vaulted'], [
        'Type and height?',
        'Vaulted or flat?',
        'Ceiling fan?',
      ]),
      el('lr_walls', 'Walls', ['wall', 'walls', 'drywall', 'plaster'], [
        'Number of walls?',
        'Damage height?',
        'Any accent features (wainscoting, chair rail)?',
      ]),
      el('lr_flooring', 'Flooring', ['floor', 'flooring', 'carpet', 'hardwood', 'laminate', 'tile', 'lvp'], [
        'Material type?',
        'Square footage?',
        'Transition strips needed?',
      ]),
      el('lr_paint', 'Paint / Finish', ['paint', 'prime', 'finish', 'texture'], [
        'Walls, ceiling, or both?',
        'Texture match?',
      ]),
      el('lr_baseboard', 'Baseboard / Trim', ['baseboard', 'trim', 'molding', 'crown'], [
        'Linear feet?',
        'Profile match?',
        'Crown molding?',
      ]),
      el('lr_electrical', 'Electrical', ['electrical', 'outlet', 'switch', 'light', 'fixture'], [
        'Outlets/switches?',
        'Light fixtures?',
      ]),
      el('lr_window', 'Windows', ['window', 'glass', 'blinds', 'curtain'], [
        'Number of windows?',
        'Coverings?',
      ]),
      el('lr_door', 'Door(s)', ['door', 'entry', 'sliding', 'french'], [
        'Type (entry, sliding, French)?',
        'Size?',
      ]),
      el('lr_fireplace', 'Fireplace', ['fireplace', 'mantle', 'hearth', 'chimney'], [
        'Type (gas, wood, electric)?',
        'Mantle/surround damage?',
      ]),
    ],
  },

  dining_room: {
    elements: [
      el('dr_ceiling', 'Ceiling', ['ceiling', 'overhead', 'chandelier'], [
        'Type and height?',
        'Chandelier/fixture?',
      ]),
      el('dr_walls', 'Walls', ['wall', 'walls', 'drywall', 'wainscoting', 'chair rail'], [
        'Walls affected?',
        'Wainscoting or chair rail?',
      ]),
      el('dr_flooring', 'Flooring', ['floor', 'flooring', 'hardwood', 'tile', 'carpet'], [
        'Material type?',
        'Square footage?',
      ]),
      el('dr_paint', 'Paint / Finish', ['paint', 'prime', 'finish'], [
        'Walls, ceiling, or both?',
      ]),
      el('dr_baseboard', 'Baseboard / Trim', ['baseboard', 'trim', 'molding', 'crown'], [
        'Linear feet?',
        'Crown molding?',
      ]),
      el('dr_electrical', 'Electrical', ['electrical', 'outlet', 'light', 'chandelier', 'fixture'], [
        'Chandelier/fixture?',
        'Outlets?',
      ]),
      el('dr_window', 'Windows', ['window', 'glass', 'blinds'], [
        'Number and size?',
      ]),
    ],
  },

  hallway: {
    elements: [
      el('hw_ceiling', 'Ceiling', ['ceiling', 'overhead'], [
        'Type?',
        'Light fixtures?',
      ]),
      el('hw_walls', 'Walls', ['wall', 'walls', 'drywall'], [
        'Linear feet?',
        'Both sides?',
      ]),
      el('hw_flooring', 'Flooring', ['floor', 'flooring', 'carpet', 'hardwood', 'tile'], [
        'Material?',
        'Square footage?',
      ]),
      el('hw_paint', 'Paint / Finish', ['paint', 'prime', 'finish'], [
        'Walls and ceiling?',
      ]),
      el('hw_baseboard', 'Baseboard / Trim', ['baseboard', 'trim', 'molding'], [
        'Linear feet (both sides)?',
      ]),
      el('hw_door', 'Doors', ['door', 'doors'], [
        'Number of doors?',
        'Type and size?',
      ]),
    ],
  },

  laundry_room: {
    elements: [
      el('la_ceiling', 'Ceiling', ['ceiling', 'overhead'], [
        'Type?',
        'Light fixture?',
      ]),
      el('la_walls', 'Walls', ['wall', 'walls', 'drywall'], [
        'Walls affected?',
        'Damage height?',
      ]),
      el('la_flooring', 'Flooring', ['floor', 'flooring', 'tile', 'vinyl', 'concrete'], [
        'Material type?',
        'Square footage?',
      ]),
      el('la_plumbing', 'Plumbing', ['plumbing', 'pipe', 'valve', 'hose bib', 'drain', 'supply'], [
        'Supply lines?',
        'Drain connections?',
        'Shut-off valves?',
      ]),
      el('la_electrical', 'Electrical', ['electrical', 'outlet', 'gfci', '220', '240', 'dryer'], [
        'GFCI outlets?',
        '220/240V dryer outlet?',
      ]),
      el('la_appliances', 'Appliances', ['washer', 'dryer', 'appliance'], [
        'Disconnect/reconnect needed?',
        'Replacement?',
      ]),
      el('la_cabinets', 'Cabinets / Shelving', ['cabinet', 'shelf', 'shelving', 'storage'], [
        'Type and linear feet?',
      ]),
      el('la_paint', 'Paint / Finish', ['paint', 'prime', 'finish'], [
        'Moisture-resistant?',
      ]),
    ],
  },

  closet: {
    elements: [
      el('cl_walls', 'Walls', ['wall', 'walls', 'drywall'], [
        'Walls affected?',
      ]),
      el('cl_flooring', 'Flooring', ['floor', 'flooring', 'carpet', 'pad'], [
        'Material?',
        'Square footage?',
      ]),
      el('cl_shelving', 'Shelving / Rod', ['shelf', 'shelving', 'rod', 'organizer', 'wire'], [
        'Type (wire, wood, custom)?',
        'Linear feet?',
      ]),
      el('cl_paint', 'Paint / Finish', ['paint', 'prime', 'finish'], [
        'Walls and ceiling?',
      ]),
      el('cl_door', 'Door', ['door', 'bi-fold', 'sliding', 'bifold'], [
        'Type (hinged, bi-fold, sliding)?',
        'Size?',
      ]),
    ],
  },

  office: {
    elements: [
      el('of_ceiling', 'Ceiling', ['ceiling', 'overhead'], [
        'Type?',
        'Light fixtures?',
      ]),
      el('of_walls', 'Walls', ['wall', 'walls', 'drywall'], [
        'Walls affected?',
        'Any built-ins?',
      ]),
      el('of_flooring', 'Flooring', ['floor', 'flooring', 'carpet', 'hardwood'], [
        'Material?',
        'Square footage?',
      ]),
      el('of_electrical', 'Electrical', ['electrical', 'outlet', 'data', 'cable', 'light'], [
        'Data/cable outlets?',
        'Power outlets?',
        'Fixtures?',
      ]),
      el('of_paint', 'Paint / Finish', ['paint', 'prime', 'finish'], [
        'Walls and ceiling?',
      ]),
      el('of_baseboard', 'Baseboard / Trim', ['baseboard', 'trim', 'molding'], [
        'Linear feet?',
      ]),
    ],
  },

  garage: {
    elements: [
      el('ga_ceiling', 'Ceiling', ['ceiling', 'overhead', 'rafters', 'drywall'], [
        'Finished or unfinished?',
        'Insulation?',
      ]),
      el('ga_walls', 'Walls', ['wall', 'walls', 'drywall', 'osb', 'plywood'], [
        'Finished or unfinished?',
        'Fire-rated drywall?',
      ]),
      el('ga_flooring', 'Flooring', ['floor', 'flooring', 'concrete', 'slab', 'epoxy'], [
        'Concrete condition?',
        'Coating/epoxy?',
      ]),
      el('ga_door', 'Garage Door', ['garage door', 'overhead door', 'opener', 'track', 'spring'], [
        'Single or double?',
        'Material?',
        'Opener replacement?',
      ]),
      el('ga_electrical', 'Electrical', ['electrical', 'outlet', 'light', 'panel', 'gfci'], [
        'Panel location?',
        'GFCI outlets?',
        'Lighting?',
      ]),
      el('ga_insulation', 'Insulation', ['insulation', 'blown', 'batt', 'r-value'], [
        'Type and R-value?',
        'Areas affected?',
      ]),
    ],
  },

  roof: {
    elements: [
      el('rf_shingles', 'Shingles / Covering', ['shingle', 'shingles', 'tile', 'metal', 'slate', 'roof cover'], [
        'Material (asphalt, tile, metal)?',
        'Square footage or number of squares?',
        'Age of roof?',
      ]),
      el('rf_decking', 'Decking', ['decking', 'deck', 'plywood', 'osb', 'sheathing'], [
        'Material?',
        'Sheets needing replacement?',
      ]),
      el('rf_underlayment', 'Underlayment', ['underlayment', 'felt', 'ice shield', 'synthetic'], [
        'Type (felt, synthetic, ice/water shield)?',
      ]),
      el('rf_flashing', 'Flashing', ['flashing', 'drip edge', 'valley', 'step flash'], [
        'Types needed (drip edge, valley, step)?',
        'Linear feet?',
      ]),
      el('rf_ridge', 'Ridge / Vent', ['ridge', 'vent', 'ridge vent', 'turbine', 'soffit vent'], [
        'Ridge vent replacement?',
        'Other ventilation?',
      ]),
      el('rf_gutter', 'Gutters / Downspouts', ['gutter', 'downspout', 'fascia', 'soffit'], [
        'Linear feet?',
        'Material?',
        'Fascia/soffit damage?',
      ]),
      el('rf_chimney', 'Chimney / Penetrations', ['chimney', 'skylight', 'vent pipe', 'boot'], [
        'Flashing around penetrations?',
        'Chimney cap?',
        'Pipe boots?',
      ]),
    ],
  },

  exterior: {
    elements: [
      el('ex_siding', 'Siding', ['siding', 'vinyl', 'hardie', 'stucco', 'wood', 'lap'], [
        'Material type?',
        'Square footage?',
        'Number of stories?',
      ]),
      el('ex_trim', 'Exterior Trim', ['trim', 'fascia', 'soffit', 'corner', 'frieze'], [
        'Material?',
        'Linear feet?',
      ]),
      el('ex_paint', 'Paint / Finish', ['paint', 'prime', 'stain', 'exterior finish'], [
        'Areas to paint?',
        'Number of stories?',
      ]),
      el('ex_windows', 'Windows', ['window', 'glass', 'frame', 'screen'], [
        'Number and size?',
        'Screens?',
        'Full replacement or glass only?',
      ]),
      el('ex_doors', 'Exterior Doors', ['door', 'entry', 'storm door', 'sliding'], [
        'Type and size?',
        'Hardware?',
        'Storm door?',
      ]),
      el('ex_fence', 'Fence / Gate', ['fence', 'gate', 'post', 'picket', 'panel'], [
        'Material?',
        'Linear feet?',
        'Gate replacement?',
      ]),
      el('ex_concrete', 'Concrete / Walkway', ['concrete', 'driveway', 'walkway', 'patio', 'slab', 'sidewalk'], [
        'Type (driveway, walkway, patio)?',
        'Square footage?',
      ]),
    ],
  },

  general_conditions: {
    elements: [
      el('gc_demo', 'Demolition', ['demo', 'demolition', 'tear out', 'remove'], [
        'Scope of demolition?',
        'Haul-off needed?',
      ]),
      el('gc_dumpster', 'Dumpster / Debris', ['dumpster', 'debris', 'haul', 'disposal'], [
        'Size needed?',
        'Number of loads?',
      ]),
      el('gc_protection', 'Protection / Containment', ['protect', 'containment', 'plastic', 'zip wall', 'barrier'], [
        'Areas to protect?',
        'Type of containment?',
      ]),
      el('gc_permits', 'Permits', ['permit', 'inspection', 'code'], [
        'Types of permits needed?',
      ]),
      el('gc_temporary', 'Temporary Repairs', ['temporary', 'tarp', 'board up', 'emergency', 'temp power'], [
        'Type of temporary repair?',
        'Duration?',
      ]),
      el('gc_equipment', 'Equipment', ['equipment', 'scaffold', 'lift', 'dehumidifier', 'fan', 'air mover'], [
        'Equipment needed?',
        'Duration of use?',
      ]),
    ],
  },

  hvac: {
    elements: [
      el('hv_unit', 'HVAC Unit', ['hvac', 'ac', 'furnace', 'heat pump', 'air handler', 'condenser'], [
        'System type (split, package, heat pump)?',
        'Tonnage/BTU?',
        'Age of system?',
      ]),
      el('hv_ductwork', 'Ductwork', ['duct', 'ductwork', 'flex duct', 'supply', 'return'], [
        'Type (flex, rigid)?',
        'Linear feet?',
        'Insulation wrap?',
      ]),
      el('hv_thermostat', 'Thermostat', ['thermostat', 'smart thermostat', 'programmable'], [
        'Type (basic, programmable, smart)?',
      ]),
      el('hv_register', 'Registers / Grilles', ['register', 'grille', 'vent', 'diffuser', 'return air'], [
        'Number and size?',
        'Supply or return?',
      ]),
      el('hv_refrigerant', 'Refrigerant / Electrical', ['refrigerant', 'freon', 'line set', 'disconnect', 'whip'], [
        'Refrigerant type?',
        'Line set replacement?',
        'Electrical disconnect?',
      ]),
    ],
  },

  stairs: {
    elements: [
      el('st_treads', 'Treads / Risers', ['tread', 'riser', 'stair', 'step', 'nose'], [
        'Number of treads?',
        'Material (carpet, hardwood, tile)?',
        'Nosing type?',
      ]),
      el('st_railing', 'Railing / Balusters', ['railing', 'rail', 'baluster', 'spindle', 'newel', 'handrail'], [
        'Linear feet?',
        'Material?',
        'Balusters count?',
      ]),
      el('st_walls', 'Stairwell Walls', ['wall', 'walls', 'drywall', 'stairwell'], [
        'Both sides?',
        'Height?',
      ]),
      el('st_paint', 'Paint / Finish', ['paint', 'prime', 'finish', 'stain'], [
        'Walls, trim, or stain?',
      ]),
    ],
  },

  porch: {
    elements: [
      el('po_decking', 'Decking / Floor', ['deck', 'decking', 'floor', 'concrete', 'tile', 'wood'], [
        'Material?',
        'Square footage?',
      ]),
      el('po_ceiling', 'Porch Ceiling', ['ceiling', 'beadboard', 'soffit', 'overhead'], [
        'Material?',
        'Square footage?',
      ]),
      el('po_railing', 'Railing / Posts', ['railing', 'rail', 'post', 'column', 'baluster'], [
        'Material?',
        'Linear feet?',
      ]),
      el('po_roof', 'Porch Roof', ['roof', 'overhang', 'cover'], [
        'Attached or freestanding?',
        'Material?',
      ]),
      el('po_screen', 'Screening', ['screen', 'screened', 'mesh'], [
        'Square footage?',
        'Frame replacement?',
      ]),
      el('po_paint', 'Paint / Finish', ['paint', 'prime', 'stain', 'finish'], [
        'Areas to paint/stain?',
      ]),
    ],
  },

  sitting_room: {
    elements: [
      el('sr_ceiling', 'Ceiling', ['ceiling', 'overhead'], [
        'Type and height?',
      ]),
      el('sr_walls', 'Walls', ['wall', 'walls', 'drywall'], [
        'Walls affected?',
      ]),
      el('sr_flooring', 'Flooring', ['floor', 'flooring', 'carpet', 'hardwood'], [
        'Material?',
        'Square footage?',
      ]),
      el('sr_paint', 'Paint / Finish', ['paint', 'prime', 'finish'], [
        'Walls, ceiling, or both?',
      ]),
      el('sr_baseboard', 'Baseboard / Trim', ['baseboard', 'trim', 'molding'], [
        'Linear feet?',
      ]),
      el('sr_electrical', 'Electrical', ['electrical', 'outlet', 'light'], [
        'Outlets and fixtures?',
      ]),
    ],
  },

  sunroom: {
    elements: [
      el('su_ceiling', 'Ceiling', ['ceiling', 'overhead', 'skylight'], [
        'Type?',
        'Skylights?',
      ]),
      el('su_walls', 'Walls / Glass', ['wall', 'walls', 'glass', 'panel', 'window'], [
        'Glass panels affected?',
        'Wall sections?',
      ]),
      el('su_flooring', 'Flooring', ['floor', 'flooring', 'tile', 'concrete'], [
        'Material?',
        'Square footage?',
      ]),
      el('su_electrical', 'Electrical', ['electrical', 'outlet', 'light', 'fan'], [
        'Outlets?',
        'Ceiling fan?',
      ]),
    ],
  },

  storage: {
    elements: [
      el('sg_walls', 'Walls', ['wall', 'walls', 'drywall', 'plywood', 'osb'], [
        'Finished or unfinished?',
      ]),
      el('sg_flooring', 'Flooring', ['floor', 'flooring', 'concrete', 'plywood'], [
        'Material?',
        'Square footage?',
      ]),
      el('sg_shelving', 'Shelving', ['shelf', 'shelving', 'storage'], [
        'Type and linear feet?',
      ]),
      el('sg_door', 'Door', ['door', 'entry', 'overhead'], [
        'Type and size?',
      ]),
    ],
  },

  contents: {
    elements: [
      el('co_furniture', 'Furniture', ['furniture', 'sofa', 'couch', 'table', 'chair', 'bed', 'dresser', 'desk'], [
        'List of affected items?',
        'Cleanable or replace?',
      ]),
      el('co_electronics', 'Electronics', ['electronic', 'tv', 'computer', 'stereo', 'speaker'], [
        'Items and condition?',
      ]),
      el('co_clothing', 'Clothing / Textiles', ['clothing', 'clothes', 'textile', 'linens', 'curtains'], [
        'Volume (bags, boxes)?',
        'Professional cleaning or replace?',
      ]),
      el('co_manipulation', 'Content Manipulation', ['move', 'pack', 'store', 'manipulate', 'relocate'], [
        'Move out, pack out, or in-place?',
        'Storage needed?',
        'Number of rooms?',
      ]),
    ],
  },
};

/**
 * Retrieves the checklist for a given room type.
 * Falls back to an empty checklist if the room type is not found.
 *
 * @param roomType - The canonical room type key.
 * @returns The RoomChecklist for the given room type.
 */
export function getChecklist(roomType: string): RoomChecklist {
  return ROOM_CHECKLISTS[roomType] || { elements: [] };
}

/**
 * Filters checklist elements to only those NOT mentioned in the description.
 * Uses case-insensitive keyword matching against the description text.
 *
 * @param roomType - The canonical room type key.
 * @param description - The free-text room description.
 * @returns Array of ChecklistElements whose keywords were not found.
 */
export function getUnmentionedElements(
  roomType: string,
  description: string
): ChecklistElement[] {
  const checklist = getChecklist(roomType);
  if (!checklist.elements.length || !description.trim()) {
    return checklist.elements;
  }

  const lowerDesc = description.toLowerCase();
  return checklist.elements.filter(
    (element) => !element.keywords.some((kw) => lowerDesc.includes(kw.toLowerCase()))
  );
}
